import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useInstanceId } from '../../contexts/InstanceContext';
import API_ENDPOINTS from '../../config/api';
import { computeStructuralMetrics, rankLeverageCandidates, getQuadrantMeta } from '../../utils/structuralAnalysis';
import ReachMapView from '../../components/PivotPoints/ReachMapView';
import SystemsViewScaffold from './SystemsViewScaffold';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * Phase 1 — Rakenneanalyysi (MICMAC) / structural pivot analysis.
 *
 * The STRUCTURAL counterpart to the semantic pivot points: pure runtime, one
 * /api/pathways fetch, no storage. Computes influence×dependence (MICMAC) over
 * the impact-path DAG and surfaces the relay quadrant as structural leverage
 * candidates. Shares its maths with the planned method-toggle in
 * PivotPointsScreen via utils/structuralAnalysis.js. See
 * specs/systems-analysis-visualisation.md.
 */

const Stat = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 20px;
  margin: 0 12px 12px 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);

  .num { font-size: 1.8rem; font-weight: 700; color: #fff; }
  .lbl { font-size: 0.8rem; color: rgba(255, 255, 255, 0.55); }
`;

const RankList = styled.div`
  margin-top: 24px;

  .head { display: flex; align-items: baseline; gap: 12px; margin-bottom: 10px; }
  h3 { font-size: 1rem; margin: 0; color: #fff; }
  .toggle {
    font-size: 0.8rem;
    color: #2196F3;
    cursor: pointer;
    user-select: none;
    background: none;
    border: none;
    padding: 0;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 6px;

    .badge { flex-shrink: 0; font-size: 0.7rem; padding: 2px 8px; border-radius: 999px; font-weight: 600; }
    .deg { flex-shrink: 0; color: rgba(255,255,255,0.5); font-size: 0.8rem; min-width: 92px; }
    .txt { color: rgba(255, 255, 255, 0.85); font-size: 0.9rem; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.05rem;
  color: #fff;
  margin: 28px 0 12px;
  font-weight: 600;
`;

export default function PivotNetworkScreen() {
  const [searchParams] = useSearchParams();
  const instanceId = useInstanceId(searchParams);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [excludeStarters, setExcludeStarters] = useState(true);

  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    setState({ loading: true, error: null, data: null });

    fetch(API_ENDPOINTS.PATHWAYS(`kid=${instanceId}`))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => { if (!cancelled) setState({ loading: false, error: null, data: json }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message, data: null }); });

    return () => { cancelled = true; };
  }, [instanceId]);

  const { t, language } = useTranslation();
  const quadrantMeta = useMemo(() => getQuadrantMeta(language), [language]);

  const result = useMemo(
    () => (state.data
      ? computeStructuralMetrics(state.data.pathways, state.data.connections, { language })
      : null),
    [state.data, language]
  );

  const ranked = useMemo(
    () => (result ? rankLeverageCandidates(result.nodes, { excludeStarters, limit: 12 }) : []),
    [result, excludeStarters]
  );

  return (
    <SystemsViewScaffold viewKey="pivotNetwork">
      <div style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
        {state.loading && <p>{t('pivotPoints.structural.loadingPathways')}</p>}
        {state.error && <p style={{ color: '#ff6b6b' }}>{t('pivotPoints.structural.loadFailed', { error: state.error })}</p>}

        {result && (
          <>
            <div>
              <Stat><span className="num">{result.summary.nodeCount}</span><span className="lbl">{t('pivotPoints.structural.nodes')}</span></Stat>
              <Stat><span className="num">{result.summary.edgeCount}</span><span className="lbl">{t('pivotPoints.structural.edges')}</span></Stat>
              <Stat><span className="num">{result.summary.quadrantCounts.relay}</span><span className="lbl">{t('pivotPoints.structural.relays')}</span></Stat>
              <Stat><span className="num">{result.summary.convergenceCount}</span><span className="lbl">{t('pivotPoints.structural.convergences')}</span></Stat>
            </div>

            <SectionTitle>{t('pivotPoints.structural.coverageMapTitle')}</SectionTitle>
            <ReachMapView result={result} />

            <RankList>
              <div className="head">
                <h3>{t('pivotPoints.structural.candidatesTitle')}</h3>
                <button className="toggle" onClick={() => setExcludeStarters((v) => !v)}>
                  {excludeStarters ? t('pivotPoints.structural.showStarters') : t('pivotPoints.structural.hideStarters')}
                </button>
              </div>
              {ranked.map((e) => (
                <div className="row" key={e.id}>
                  <span className="badge" style={{ background: `${quadrantMeta[e.quadrant].color}22`, color: quadrantMeta[e.quadrant].color }}>
                    {quadrantMeta[e.quadrant].label}
                  </span>
                  <span className="deg">{t('pivotPoints.structural.influenceDep', { influence: e.influence, dependence: e.dependence })}</span>
                  <span className="txt">{e.label}</span>
                </div>
              ))}
              {ranked.length === 0 && (
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>{t('pivotPoints.structural.empty')}</p>
              )}
            </RankList>

            <p style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)' }}>
              {t('pivotPoints.structural.networkFooter')}
            </p>
          </>
        )}
      </div>
    </SystemsViewScaffold>
  );
}
